import { useEffect, useRef, useState, type ReactNode } from "react";

interface Props {
  children: ReactNode;
  onClear: () => void;
  defaultWidth?: number;
  /**
   * Namespaces the persisted width + collapsed state so screens that share this component (requests
   * list, task efficiency) remember their rail independently. Defaults to "requests" to keep the
   * existing width key (`ob-rail-width-requests`) byte-identical.
   */
  storageKey?: string;
}

/**
 * Resizable + collapsible filter rail. Class names match the prototype's verbatim-copied CSS
 * (`.filter-rail`, `.filter-rail-head`, `.filter-rail-body`, `.filter-rail-head-actions .clear`,
 * `.rail-collapse-btn`, `.resize-handle`, `.filter-rail-collapsed .vlabel`).
 *
 * Both the preferred width and the collapsed/expanded choice persist in localStorage so they survive
 * reload.
 */
export function FilterRail({ children, onClear, defaultWidth = 240, storageKey = "requests" }: Props) {
  const widthKey = `ob-rail-width-${storageKey}`;
  const collapsedKey = `ob-rail-collapsed-${storageKey}`;

  const [width, setWidth] = useState<number>(() => {
    if (typeof window === "undefined") return defaultWidth;
    const stored = window.localStorage.getItem(widthKey);
    if (!stored) return defaultWidth;
    const n = Number(stored);
    return Number.isFinite(n) && n > 0 ? n : defaultWidth;
  });
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(collapsedKey) === "1";
  });
  const railRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(widthKey, String(width));
    }
  }, [widthKey, width]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(collapsedKey, collapsed ? "1" : "0");
    }
  }, [collapsedKey, collapsed]);

  const onResizeDown = (e: React.MouseEvent) => {
    e.preventDefault();
    // The rail sits on the right (order: 2 in CSS); resize handle is at its left edge, so we
    // grow when the mouse moves left. Use the rail's right edge as the anchor.
    const rect = railRef.current?.getBoundingClientRect();
    if (!rect) return;
    const right = rect.right;
    const onMove = (ev: MouseEvent) => {
      const next = Math.max(200, Math.min(window.innerWidth * 0.25, right - ev.clientX));
      setWidth(next);
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    document.body.style.cursor = "ew-resize";
    document.body.style.userSelect = "none";
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  if (collapsed) {
    return (
      <aside
        className="filter-rail filter-rail-collapsed"
        onClick={() => setCollapsed(false)}
        title="Show filters"
      >
        <span className="material-symbols-rounded" style={{ fontSize: 18 }}>
          right_panel_open
        </span>
        <span className="vlabel">Filters</span>
      </aside>
    );
  }

  return (
    <aside className="filter-rail" ref={railRef} style={{ width }}>
      <div className="resize-handle" onMouseDown={onResizeDown} title="Drag to resize" />
      <div className="filter-rail-head">
        <h4>Filters</h4>
        <div className="filter-rail-head-actions">
          <span className="clear" onClick={onClear} role="button" tabIndex={0}>
            Clear all
          </span>
          <button
            type="button"
            className="rail-collapse-btn"
            onClick={() => setCollapsed(true)}
            title="Collapse filters"
            aria-label="Collapse filters"
          >
            <span className="material-symbols-rounded" style={{ fontSize: 18 }}>
              right_panel_close
            </span>
          </button>
        </div>
      </div>
      <div className="filter-rail-body">{children}</div>
    </aside>
  );
}
